import axios, { isAxiosError } from 'axios';
import { logger } from '@/core/logger/logger';
import { invalidateSession, isCredentialRequest, isDefinitiveSessionError, requestSessionToken } from './session-policy';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.oryacrm.com/api/manager',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let sessionVerification: { token: string; promise: Promise<void> } | null = null;

async function verifySession(token: string) {
  if (sessionVerification?.token === token) return sessionVerification.promise;
  const promise = api.get('/auth/me').then(() => undefined).catch(() => {
    // The profile request's interceptor handles confirmed authentication errors.
    // Unavailable API/network responses must not erase a locally established session.
    logger.warn('[api] Session verification failed');
  });
  const verification = { token, promise };
  sessionVerification = verification;
  try { await promise; }
  finally { if (sessionVerification === verification) sessionVerification = null; }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.code === 'ECONNABORTED') {
      logger.error('[api] Request timeout');
    } else if (error.message === 'Network Error') {
      logger.error('[api] Network error — check API availability and CORS');
    }
    const token = requestSessionToken(error);
    if (token && token === localStorage.getItem('token')) {
      if (isDefinitiveSessionError(error)) {
        if (invalidateSession(token) && window.location.pathname !== '/login') window.location.href = '/login';
      } else if (isAxiosError(error) && error.response?.status === 401 && !isCredentialRequest(error)) {
        // A feature may return 401 for a domain error; confirm identity before logging out.
        await verifySession(token);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
